import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, deleteOffering, listOfferings, putOffering } from './api/client';
import type { OfferingResponse } from './api/types';

// Mirrors the backend's accepted period units (OfferingContracts.ToPeriod):
// month/week are calendar; day/hour/minute/second become a fixed span.
const PERIOD_UNITS = ['month', 'week', 'day', 'hour', 'minute', 'second'] as const;

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

const left = { textAlign: 'left' } as const;

/** Manage one tenant's product-access offerings (list / create / replace / delete). */
export function OfferingsPanel({ tenant }: { tenant: string }) {
  const [offerings, setOfferings] = useState<OfferingResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // create/replace form
  const [accessId, setAccessId] = useState('');
  const [unit, setUnit] = useState<string>('month');
  const [amount, setAmount] = useState('1');
  const [price, setPrice] = useState('9.99');
  const [currency, setCurrency] = useState('USD');

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setOfferings(await listOfferings(tenant));
    } catch (e: unknown) {
      setError(describeError(e));
      setOfferings([]);
    } finally {
      setBusy(false);
    }
  }

  // Reload whenever the acting-as tenant changes.
  useEffect(() => {
    void load();
  }, [tenant]);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await putOffering(tenant, accessId, {
        period: { unit, amount: Number(amount) },
        price: { amount: Number(price), currency },
      });
      setAccessId('');
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteOffering(tenant, id);
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>
        Offerings for &ldquo;{tenant}&rdquo;{' '}
        <button onClick={() => void load()} disabled={busy}>
          Reload
        </button>
      </h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={left}>Access ID</th>
            <th style={left}>Period</th>
            <th style={left}>Price</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {offerings.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <em>No offerings for &ldquo;{tenant}&rdquo;.</em>
              </td>
            </tr>
          ) : (
            offerings.map((o) => (
              <tr key={o.accessId}>
                <td>{o.accessId}</td>
                <td>
                  {o.period.amount} {o.period.unit}
                </td>
                <td>
                  {o.price.amount} {o.price.currency}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => void onDelete(o.accessId)} disabled={busy}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3>Create / replace</h3>
      <form onSubmit={(e) => void onSave(e)} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input required placeholder="access id" value={accessId} onChange={(e) => setAccessId(e.currentTarget.value)} />
        <input
          required
          type="number"
          min="1"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
          style={{ width: 70 }}
        />
        <select value={unit} onChange={(e) => setUnit(e.currentTarget.value)}>
          {PERIOD_UNITS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        <input
          required
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.currentTarget.value)}
          style={{ width: 90 }}
        />
        <input required maxLength={3} value={currency} onChange={(e) => setCurrency(e.currentTarget.value)} style={{ width: 64 }} />
        <button type="submit" disabled={busy}>
          Save
        </button>
      </form>
    </section>
  );
}
